import { postApi } from 'api/fetch'
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { AuthInfo } from 'utils/auth'

export const AuthManager = (props: {
  children: (auth: { authInfo: AuthInfo | undefined; authFailed: boolean }) => ReactElement | null
}) => {
  const { children } = props
  const location = useLocation()
  const history = useHistory()

  const [authInfo, setAuthInfo] = useState<AuthInfo>()
  const [authFailed, setAuthFailed] = useState(false)

  // 1. Check to see if we've finished authenticating.
  // Not authenticated yet if:
  // - there's a Drupal auth token on the URL (that means we have a new login request)
  // - we haven't yet fetched authInfo from the back end.
  const authToken = new URLSearchParams(location.search).get('token')
  const isAuthenticated = Boolean(!authFailed && !authToken && authInfo)

  // A function that checks to see if we have an existing valid Django auth session.
  // The session cookie is set to HttpOnly, which means that it's invisible from
  // JS. So we check by sending a request to the `/api/auth` endpoint and seeing
  // if it is successful or not.
  const checkAuthSession = useCallback(async () => {
    const { api_data: authResponse } = await postApi('/api/auth/v1', {})
    setAuthInfo(authResponse)
  }, [])

  // A function that tries to initiate an Django auth session, using the auth
  // token from the URL.
  const checkAuthToken = useCallback(
    async (token: string) => {
      const { api_data: authResponse } = await postApi('/api/auth/v1', {
        token,
      })
      setAuthInfo(authResponse)

      // Clear the auth token from the URL.
      history.replace({
        ...history.location,
        search: '',
      })
    },
    [history]
  )

  useEffect(() => {
    // 2. We haven't yet finished authenticating...
    if (!isAuthenticated) {
      if (authToken) {
        // 2a. We have an auth token in the URL. Try to authenticate with that.
        checkAuthToken(authToken).catch((e) => {
          console.log('Error authenticating with token', e)
          setAuthFailed(true)
        })
      } else {
        // 2b. No auth token in the URL. Check to see if we already have a
        // valid Django session cookie.
        checkAuthSession().catch((e) => {
          console.log('Error validating session', e)
          setAuthFailed(true)
        })
      }
    }
  }, [authToken, checkAuthSession, checkAuthToken, isAuthenticated])

  const renderProps = useMemo(
    () => ({ authInfo: isAuthenticated ? authInfo : undefined, authFailed }),
    [authFailed, authInfo, isAuthenticated]
  )

  return children(renderProps)
}
